import React, {Fragment, useEffect, useState} from 'react';
import {useQueryClient} from "react-query";
import {ComboSelect, FormDialog, FormMemo, MaskedNumber, MaskedText, Text, DatePicker }from "../../../../controls";
import { useUpdateTripContractInvoice } from '../../../../services/trips';
import {fetch as fetchTripContractInvoices} from '../../../../services/trips/useTripContractInvoices';
import { Grid } from '@material-ui/core';
import CompanyUuidLookup from '../../../Companies/components/CompanyUuidLookup';
import CurrencyLookup from '../../../Currencies/components/CurrencyLookup';
import moment from 'moment';
import { fetch as fetchExchangeRateConversion } from '../../../../services/currencies/useExchangeRateConversion';

const TripContractInvoiceDialog = (props) => {

  const classes = props.classes;
  const SystemData = props.systemData;
  const queryClient = useQueryClient();
  const eventBus = SystemData.eventBus;

  const dateFormat = "DD/MM/YYYY";

  const [dialogState, setDialogState] = useState({
    showDialog: false,
    canEdit: false,
    tripUuid: null,
    contractUuid: null,
    title: 'Trips Contract Invoice',
    dataLoaded: false,
    record: null
  });
  
  const handleUpdateTripContractInvoice = useUpdateTripContractInvoice();
  
  useEffect(()=> {
    const open = async (params) => {
      console.log("open", params);
      const {canEdit, tripUuid, contractUuid} = params;
      fetchTripContractInvoices({queryClient, extraKeys: [tripUuid, contractUuid]})
      .then((record) => {
        console.log("record", record);
        setDialogState(
          {
            ...dialogState,
            showDialog:true,
            record,
            canEdit
          }
        );  
      });
    }
    eventBus.bind("TripContractInvoiceDialog.open", open);
    return () => {
      eventBus.unbind("TripContractInvoiceDialog.open", open);
      queryClient.invalidateQueries("Trips");
      queryClient.invalidateQueries("TripContractInvoices");
    }
  }, [dialogState, eventBus, queryClient]);

  const getUsdPrice = async (currencyCode, currencyPrice, invoiceDate, queryClient) => {
    if (currencyCode === 'USD') return {price: currencyPrice, fromCurrencySymbol: '$', toCurrencySymbol: '$', exchangeRate: 1};
  
    const extraKeys = [
      "SYSTEM",
      currencyCode || "USD",
      "USD",
      moment.utc(invoiceDate).format("YYYY-MM-DD") || "0001-01-01",
    ];
  
    const conversion = await fetchExchangeRateConversion({extraKeys, queryClient});
    const {fromCurrencySymbol, toCurrencySymbol, conversionFactor} = conversion;
    const rate = conversionFactor||0;
    const price = currencyPrice * rate;
  
    return {price, fromCurrencySymbol, toCurrencySymbol, exchangeRate: rate};
  }

  if (dialogState.showDialog && dialogState.record) {
    return (
      <FormDialog
        title={dialogState.title}
        message=""
        open={true}
        record={dialogState.record}
        dialogOptions={{maxWidth: 1200, fullWidth: false}}
        classes={classes}
        buttons = {{
          save:{icon: "save", key: "Save", label: "Save", color: "secondary"},
          cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
        }}
        onChange={(state) => {
          if (state.buttonPressed === "save") {
            const record = state.record;
            handleUpdateTripContractInvoice.mutate({
              record: {...record},
              postSuccess: (savedContractInvoice) => {
                setDialogState({...dialogState, showDialog: false, tripUuid: null, contractUuid: null, tripContractInvoice: null, dataLoaded: false});
                if(props.afterSave) props.afterSave({...record, ...savedContractInvoice?.record?.data});
              },
              postError: (data) => {
                if (data.errorMessage) {
                  SystemData.alert("Error", `${data.errorMessage}`);
                } else {
                  SystemData.alert("Error", `An Unexpected Error has occurred.`);
                }
              }
            })
          } else {
            setDialogState({...dialogState, showDialog: false, tripUuid: null, contractUuid: null, tripContractInvoice: null, dataLoaded: false});
            if (typeof props.onClose === 'function') {props.onClose()}
          }
        }}
      >
        <Grid container alignContent="space-between" checkChildren={true} spacing={3} style={dialogState.selectCompany ? { minWidth: 500} : { minWidth: 300}}>
          <Grid item xs={2} checkChildren={true}>
            {(record) => { 
              return (   
                <Fragment key="tripNo">        
                  <Text
                    id="tripNo"
                    label="Trip"
                    shrink={true}
                    readOnly={true}
                    value={`${record.tripPrefix} ${record.tripNo}`}
                    style={{padding: 0}}
                  />
                </Fragment>
              )
            }}
          </Grid>
          <Grid item xs={2} checkChildren={true}>
            <Text
              id="contractReference"
              label="Contract"
              shrink={true}
              readOnly={true}
              style={{padding: 0}}
            />
          </Grid>
          <Grid item xs={8} checkChildren={true}>
            <CompanyUuidLookup
              companyType={"CLIENT"}
              id="companyUuid"
              label="Company"
              shrink={true}
              readOnly={true}
              type={"render"}
            />
          </Grid>
          <Grid item xs={2} checkChildren={true}>
            <DatePicker
              id="invoiceDate"
              label="Invoice Date"
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={(invoiceDate, props) => {
                const {record, recordChange} = props;
                let invoiceDueDate = moment(invoiceDate).add(record?.paymentTerms || 0, "days");
                getUsdPrice(record.currencyCode, record.invoiceAmount, invoiceDate, queryClient).then((currencyData) => {
                  const {price} = currencyData;
                  recordChange({...record, invoiceDate, invoiceDueDate, invoiceAmountUsd: price, currencyData});
                })
                recordChange({...record, invoiceDate, invoiceDueDate})
              }}
    />
          </Grid>        
         <Grid item xs={2} checkChildren={true}>
            <MaskedText
              id="invoiceNo"
              label="Invoice Number"
              mask={"i".repeat(50)}
              forceUpperCase={true}
              style={{fontSize: "0.875rem", ...props.style}}
              fullWidth
              shrink={true}
            />
          </Grid>
          <Grid item style={{flexBasis: "12.5%", maxWidth: "12.5%"}} checkChildren={true}>
            {(record) => (
              <MaskedNumber
                id="invoiceAmount"
                label={record.invoiceNo ? "Invoice Amount" : "Amount to Invoice"}
                decimalScale={2}
                fixedDecimalScale={true}
                thousandsGroupStyle=""
                thousandSeparator=""
                style={{fontSize: "0.875rem", ...props.style}}
                fullWidth
                inputLabelProps={{shrink: true}}
                onChange={(invoiceAmount, props) => {
                  const {record, recordChange} = props;
                  getUsdPrice(record.currencyCode, invoiceAmount, record.invoiceDate, queryClient).then((currencyData) => {
                    const {price} = currencyData;
                    recordChange({...record, invoiceAmount, invoiceAmountUsd: record.invoiceDate ? price : invoiceAmount, currencyData});
                  })
                  recordChange({...record, invoiceAmount});
                }}
              />            
            )}
          </Grid>    
          <Grid item xs={1} checkChildren={true}>
            <CurrencyLookup
              id="currencyCode"
              label="Currency"
              showChildCurrencies={false}
              inputLabelProps={{shrink:true}}
              style={{ fontSize: "0.875rem", textAlign: "right"}}
              onChange={(currencyCode, props) => {
                const {record, recordChange} = props;
                getUsdPrice(currencyCode, record.invoiceAmount, record.invoiceDate, queryClient).then((currencyData) => {
                  const {price} = currencyData;
                  recordChange({...record, currencyCode, invoiceAmountUsd: record.invoiceDate ? price : record.invoiceAmount, currencyData});
                })
                recordChange({...record, currencyCode});
              }}
          />
          </Grid>    
          <Grid item xs={2} checkChildren={true}>
            <DatePicker
              id="invoiceDueDate"
              label="Due Date"
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
            />
          </Grid>        
          <Grid item style={{flexBasis: "12.5%", maxWidth: "12.5%"}} checkChildren={true}>
            <MaskedNumber
              id="invoiceAmountUsd"
              label="Dollar Amount"
              decimalScale={2}
              fixedDecimalScale={true}
              thousandsGroupStyle=""
              thousandSeparator=""
              style={{fontSize: "0.875rem", ...props.style}}
              fullWidth
              inputLabelProps={{shrink: true}}
            />
          </Grid>    
          <Grid item xs={2} checkChildren={true}>
            <ComboSelect
              id="invoiceStatus"
              label="Invoice Status"
              group='INVOICE TRACKING'
              subGroup='STATUS'
              returnType="value"
              inputLabelProps={{shrink: true}}
            />
          </Grid>   
          <Grid item xs={12} checkChildren={true}>
            <FormMemo
              id="notes"
              label="Notes"
              readOnly={false}
              shrink={true}
            />
          </Grid>
        </Grid>
      </FormDialog>
    );
  }
  return null;
}

export default TripContractInvoiceDialog;
